// Keyword/synonym matching over the admin-managed FAQ database
// (api/faq.php, managed from /admin/faq) — this is the chat widget's actual
// knowledge base. Replaces an earlier version that searched a scraped,
// prerendered snapshot of the whole site (contentSearch.ts /
// content-index.json): that file only ever existed when a separate,
// occasionally-paused build step had last produced it, so the widget could
// (and did) go completely dark in production with no content-dependent
// cause at all. The FAQ database is a live PHP endpoint with no such
// separate build dependency, and every entry here is a clean,
// purpose-written Q&A pair — not a diluted multi-topic page section — so
// matching can be both simpler and far more precise.
//
// Reuses contentSearch.ts's tokenizer/stemmer/scoring primitives (the exact
// ones already tuned this session against real false-positive reports —
// stopwords for generic connectors, pet/animal synonyms, short-token exact
// demotion) rather than re-deriving them, since a FAQ question/answer pair
// is just a smaller, cleaner special case of the same "heading + body text"
// shape those were built for.

import { buildIdf, expandQueryTokens, matchScore, stem, tokenize, tokenSet, type TokenSet } from './contentSearch';
import type { FaqItem } from './faqApi';

export interface FaqMatch {
  item: FaqItem;
  score: number;
}

/** Calibrated separately from contentSearch.ts's MIN_CONFIDENT_SCORE — the
 * two corpora have different statistics (38 concentrated, single-topic
 * entries here vs ~90 diluted multi-section pages there), so there's no
 * reason the same cutoff would happen to suit both. */
export const MIN_FAQ_CONFIDENT_SCORE = 6;

interface Candidate {
  item: FaqItem;
  headingSet: TokenSet;
  textSet: TokenSet;
  categorySet: TokenSet;
}

export function searchFaqItems(items: FaqItem[], query: string, limit = 2): FaqMatch[] {
  const queryTokens = expandQueryTokens(tokenize(query));
  if (queryTokens.length === 0) return [];
  const queryStems = queryTokens.map(stem);

  const candidates: Candidate[] = items.map((item) => ({
    item,
    headingSet: tokenSet(item.question),
    textSet: tokenSet([...item.answer, item.note_text ?? ''].join(' ')),
    categorySet: tokenSet(item.category),
  }));

  const idf = buildIdf(candidates);

  const scored = candidates.map((c) => {
    let score = 0;
    for (let i = 0; i < queryTokens.length; i++) {
      const qt = queryTokens[i];
      const qs = queryStems[i];
      const weight = idf(qt);
      score += matchScore(qt, qs, c.headingSet, 3, 2.3, 1.5) * weight;
      score += matchScore(qt, qs, c.textSet, 1, 0.75, 0.5) * weight;
      // A category-level echo of the topic counts for a little, the same
      // way a page's title did in contentSearch.ts — e.g. "Property &
      // Location" backs up a location question even when "location"
      // itself isn't repeated in that exact FAQ's own question text.
      score += matchScore(qt, qs, c.categorySet, 1.2, 0.9, 0) * 0.5 * weight;
    }
    return { item: c.item, score: score * (1 + 0.15 * Math.min(queryTokens.length, 4)) };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.filter((m) => m.score > 0).slice(0, limit);
}

/** A question that's clearly asking about more than one thing at once
 * ("Can we cook food and what time is check-in?") — worth showing a second
 * answer alongside the first instead of just the single best match,
 * provided that second match is itself confident and genuinely a different
 * topic (same signal the chat widget uses to decide whether to render it). */
export function looksMultiIntent(query: string): boolean {
  const q = query.toLowerCase();
  if ((q.match(/\?/g) ?? []).length > 1) return true;
  return / and | also | as well as /.test(` ${q} `);
}
